import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, ApiError, Capabilities } from '@/api/client'
import { usePageLoader } from '@/components/LogoLoader'
import { SortableTable, type Column } from '@/components/SortableTable'
import { StatTile } from '@/components/StatTile'
import { useMe } from '@/features/auth/useAuth'
import { INPUT } from '@/features/inventory/shared'
import { fmtDate, fmtEur, intlLocale, useT } from '@/i18n'
import { billingMessages } from '@/i18n/messages/billing'
import type { OverviewRow } from './types'

type T = ReturnType<typeof useBillingT>
function useBillingT() { return useT(billingMessages) }
const eurShort = (n: number) => new Intl.NumberFormat(intlLocale(), { style: 'currency', currency: 'EUR', notation: 'compact', maximumFractionDigits: 1 }).format(n)

export function cycleLabel(t: T, c: string | null) { return c === 'monthly' ? t('monthly') : c === 'biweekly' ? t('biweekly') : c === 'weekly' ? t('weekly') : '—' }

export function DueBadge({ t, due }: { t: T; due: OverviewRow['nextDue'] }) {
  if (!due) return <span className="text-xs text-neutral-400">—</span>
  const cls = due.overdue ? 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300' : due.due ? 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300' : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300'
  return (
    <span className="text-xs">
      <span className="tabular">{fmtDate(due.from)} – {fmtDate(due.to)}</span>
      <span className={`ml-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}>{due.overdue ? t('overdue') : due.due ? t('due') : t('notDue')}</span>
    </span>
  )
}

/**
 * Customer billing overview (docs/billing-design.md): per project, what is ready to bill, what is
 * blocked by missing documents and why, and when the next statement is due.
 */
export function BillingPage() {
  const t = useBillingT()
  const navigate = useNavigate()
  const { data: me } = useMe()
  const canManage = me?.capabilities.includes(Capabilities.ManageBilling) ?? false
  const { data, isPending } = useQuery<OverviewRow[]>({ queryKey: ['billing-overview'], queryFn: () => api.get<OverviewRow[]>('/api/billing/overview') })
  const [q, setQ] = useState('')
  const [onlyContracts, setOnlyContracts] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const rows = useMemo(() => {
    const n = q.trim().toLowerCase()
    return (data ?? []).filter((r) => (!onlyContracts || r.contractId) && (!n || [r.projectName, r.projectCode, r.provider].some((x) => x?.toLowerCase().includes(n))))
  }, [data, q, onlyContracts])

  const loader = usePageLoader(isPending, t('loading'))
  if (loader) return loader
  if (!data) return null
  const withContract = data.filter((r) => r.contractId)

  const setUp = async (projectId: string) => {
    setError(null)
    try { const r = await api.post<{ id: string }>('/api/billing/contracts', { projectId }); navigate(`/billing/${r.id}`) }
    catch (e) { setError(e instanceof ApiError ? e.message : String(e)) }
  }

  const missingText = (r: OverviewRow) => Object.entries(r.missingCounts).map(([k, n]) => `${t(k as 'photos' | 'rtk' | 'lidar')} ${n}`).join(' · ')
  const columns: Column<OverviewRow>[] = [
    { key: 'project', header: t('colProject'), sortValue: (r) => r.projectName ?? '', render: (r) => (
      <div><div className="font-medium">{r.projectName ?? '—'}</div><div className="text-xs text-neutral-500">{r.projectCode}</div></div>) },
    { key: 'client', header: t('colClient'), sortValue: (r) => r.provider ?? '', render: (r) => <span className="text-xs">{r.provider ?? '—'}</span> },
    { key: 'cycle', header: t('colCycle'), render: (r) => r.contractId ? <span className="text-xs">{cycleLabel(t, r.cycle)}</span> : <span className="text-xs text-neutral-400">{t('noContract')}</span> },
    { key: 'ready', header: t('colReady'), align: 'right', sortValue: (r) => r.readyValue, render: (r) => r.contractId ? (
      <span className="tabular"><span className="font-semibold">{fmtEur(r.readyValue)}</span><span className="block text-xs text-neutral-500">{t('crewDays', { n: r.readySessions })}</span>
        {r.unpricedItems > 0 && <span className="block text-[11px] font-medium text-amber-700 dark:text-amber-300">{t('unpricedShort', { n: r.unpricedItems })}</span>}</span>) : '' },
    { key: 'blocked', header: t('colBlocked'), align: 'right', sortValue: (r) => r.blockedValue, render: (r) => r.contractId && r.blockedSessions ? (
      <span className="tabular text-amber-800 dark:text-amber-300">{fmtEur(r.blockedValue)}<span className="block text-xs">{missingText(r)}</span></span>) : '' },
    { key: 'due', header: t('colDue'), sortValue: (r) => (r.nextDue?.overdue ? 2 : r.nextDue?.due ? 1 : 0), render: (r) => r.contractId ? <DueBadge t={t} due={r.nextDue} /> : '' },
    { key: 'last', header: t('colLast'), sortValue: (r) => r.lastIssuedTo ?? '', render: (r) => <span className="tabular text-xs">{fmtDate(r.lastIssuedTo)}</span> },
    { key: 'action', header: '', render: (r) => r.contractId
      ? <Link to={`/billing/${r.contractId}`} className="text-xs font-semibold text-brand-700 hover:underline dark:text-brand-300">{t('open')}</Link>
      : canManage ? <button type="button" onClick={() => void setUp(r.projectId)} className="whitespace-nowrap rounded-lg bg-brand-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-brand-500">{t('setUp')}</button> : null },
  ]

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">{t('title')}</h1>
        <p className="mt-1 max-w-3xl text-sm text-neutral-500">{t('subtitle')}</p>
      </header>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 lg:grid-cols-5">
        <StatTile label={t('tileReady')} value={withContract.reduce((s, r) => s + r.readyValue, 0)} unit="EUR" display={eurShort(withContract.reduce((s, r) => s + r.readyValue, 0))} />
        <StatTile label={t('tileBlocked')} value={withContract.reduce((s, r) => s + r.blockedValue, 0)} unit="EUR" display={eurShort(withContract.reduce((s, r) => s + r.blockedValue, 0))} />
        <StatTile label={t('tileDue')} value={withContract.filter((r) => r.nextDue?.due).length} unit="" />
        <StatTile label={t('tileOverdue')} value={withContract.filter((r) => r.nextDue?.overdue).length} unit="" />
        <StatTile label={t('tileBilled')} value={withContract.reduce((s, r) => s + r.billedNet, 0)} unit="EUR" display={eurShort(withContract.reduce((s, r) => s + r.billedNet, 0))} />
      </div>
      {error && <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{error}</p>}
      <section className="space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('search')} aria-label={t('search')} className={`${INPUT} basis-full sm:basis-80`} />
          <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={onlyContracts} onChange={(e) => setOnlyContracts(e.target.checked)} className="size-4" />{t('onlyContracts')}</label>
          <span className="ml-auto text-xs text-neutral-500">{t('shownOf', { shown: rows.length, total: data.length })}</span>
        </div>
        <div className="overflow-x-auto rounded-2xl bg-white ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
          <SortableTable columns={columns} rows={rows} rowKey={(r) => r.projectId} />
        </div>
      </section>
    </div>
  )
}
